<template>
  <b-card
    v-if="viewMode === 'my'"
    class="mb-0 my-view-calendar-card"
    :class="{ 'calendar-compact': compact, 'calendar-full-width': !compact }"
  >
    <div class="d-flex justify-content-between align-items-center flex-wrap calendar-toolbar">
      <h5 class="mb-0 calendar-section-title">{{ compact ? 'Calendar' : 'Attendance Calendar' }}</h5>
      <div class="d-flex align-items-center">
        <b-form-select
          :value="selectedMonth"
          :options="monthOptions"
          size="sm"
          class="mr-1 calendar-month-select"
          @change="$emit('update:selectedMonth', $event)"
        />
        <b-form-select
          :value="selectedYear"
          :options="yearOptions"
          size="sm"
          class="calendar-year-select"
          @change="$emit('update:selectedYear', $event)"
        />
      </div>
    </div>

    <!-- Calendar Grid -->
    <div
      ref="calendarContainer"
      class="calendar-container my-view-calendar-container"
      @click="onContainerClick"
    >
      <div class="calendar-header">
        <div v-for="day in weekDays" :key="day" class="calendar-day-header">
          {{ compact ? day.charAt(0) : day }}
        </div>
      </div>
      <div class="calendar-body">
        <div
          v-for="(week, weekIndex) in calendarWeeks"
          :key="weekIndex"
          class="calendar-week"
        >
          <div
            v-for="(day, dayIndex) in week"
            :key="dayIndex"
            class="calendar-day"
            :class="{
              'other-month': !day.isCurrentMonth,
              'today': day.isToday,
              'holiday': day.isHoliday,
              'company-wfh': day.isCompanyWfh,
              'leave': day.isLeave,
              'maternity-paternity-leave': day.isMaternityPaternityLeave,
              'other-leave': day.isOtherLeave,
              'wfh': day.isWFH,
              'present': day.isPresent,
              'absent': day.isAbsent,
              'regularization': day.isRegularization,
              'has-biometric': day.hasBiometric,
              'day-applyable': compact && isApplyableDay(day),
              'day-menu-open': applyMenu.visible && applyMenu.date === dayDate(day),
            }"
            @click.stop="onDayClick($event, day)"
          >
            <div class="day-number">{{ day.day }}</div>
          </div>
        </div>
      </div>

      <!-- Day apply popover (compact employee view) -->
      <div
        v-if="compact && applyMenu.visible"
        class="calendar-day-apply-menu"
        :style="applyMenuStyle"
        role="menu"
        @click.stop
      >
        <div class="calendar-day-apply-menu__title">
          Apply for {{ applyMenu.label }}
        </div>
        <button
          v-if="applyMenu.showFutureApplies"
          type="button"
          class="calendar-day-apply-menu__item"
          role="menuitem"
          @click="chooseApply('leave')"
        >
          <feather-icon icon="CalendarIcon" size="13" class="mr-50" />
          Apply Leave
        </button>
        <button
          v-if="applyMenu.showFutureApplies"
          type="button"
          class="calendar-day-apply-menu__item"
          role="menuitem"
          @click="chooseApply('wfh')"
        >
          <feather-icon icon="HomeIcon" size="13" class="mr-50" />
          Apply WFH
        </button>
        <button
          v-if="applyMenu.showFutureApplies"
          type="button"
          class="calendar-day-apply-menu__item"
          role="menuitem"
          @click="chooseApply('comp-off')"
        >
          <feather-icon icon="AwardIcon" size="13" class="mr-50" />
          Apply Comp Off
        </button>
        <button
          v-if="applyMenu.showRequestCompOff"
          type="button"
          class="calendar-day-apply-menu__item calendar-day-apply-menu__item--muted"
          role="menuitem"
          @click="chooseApply('request-comp-off')"
        >
          <feather-icon icon="PlusCircleIcon" size="13" class="mr-50" />
          Request Comp Off
        </button>
      </div>
    </div>

    <!-- Calendar Legend -->
    <div class="calendar-legend mb-0" :class="{ 'calendar-legend-compact': compact }">
      <span class="legend-item"><span class="legend-dot present-dot"></span>Present</span>
      <span class="legend-item"><span class="legend-dot leave-dot"></span>Leave</span>
      <span v-if="!compact" class="legend-item"><span class="legend-dot maternity-paternity-dot"></span>Maternity/Paternity</span>
      <span class="legend-item"><span class="legend-dot wfh-dot"></span>WFH</span>
      <span class="legend-item"><span class="legend-dot regularization-dot"></span>Reg.</span>
      <span class="legend-item"><span class="legend-dot holiday-dot"></span>Holiday</span>
      <span class="legend-item"><span class="legend-dot company-wfh-dot"></span>Co. WFH</span>
      <span class="legend-item"><span class="legend-dot absent-dot"></span>Absent</span>
      <span v-if="!compact" class="legend-item"><span class="legend-dot weekoff-dot"></span>Week Off</span>
    </div>

    <!-- Compact: apply from calendar actions (fills bottom of equal-height card) -->
    <div v-if="compact" class="calendar-apply-strip">
      <div class="calendar-apply-strip__hint">
        Tap a day to apply · or use quick actions
      </div>
      <div class="calendar-apply-strip__actions">
        <button type="button" class="calendar-apply-btn calendar-apply-btn--leave" @click="emitApply('leave')">
          Leave
        </button>
        <button type="button" class="calendar-apply-btn calendar-apply-btn--wfh" @click="emitApply('wfh')">
          WFH
        </button>
        <button type="button" class="calendar-apply-btn calendar-apply-btn--comp-off" @click="emitApply('comp-off')">
          Comp Off
        </button>
        <button
          type="button"
          class="calendar-apply-btn calendar-apply-btn--req-co"
          :disabled="isReqCoStripDisabled"
          @click="emitApply('request-comp-off')"
        >
          Req. CO
        </button>
      </div>
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BFormSelect,
} from 'bootstrap-vue'
import moment from 'moment'

export default {
  name: 'AttendanceCalendar',
  components: {
    BCard,
    BFormSelect,
  },
  props: {
    viewMode: {
      type: String,
      default: 'my',
    },
    compact: {
      type: Boolean,
      default: false,
    },
    selectedMonth: {
      type: Number,
      required: true,
    },
    selectedYear: {
      type: Number,
      required: true,
    },
    monthOptions: {
      type: Array,
      default: () => [],
    },
    yearOptions: {
      type: Array,
      default: () => [],
    },
    weekDays: {
      type: Array,
      default: () => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    },
    calendarWeeks: {
      type: Array,
      default: () => [],
    },
    getDayTooltip: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      applyMenu: {
        visible: false,
        date: null,
        label: '',
        x: 0,
        y: 0,
        width: 168,
        showFutureApplies: false,
        showRequestCompOff: false,
      },
      /** Last clicked applyable day — drives Req. CO strip enablement */
      selectedApplyDate: null,
    }
  },
  computed: {
    applyMenuStyle() {
      return {
        left: `${this.applyMenu.x}px`,
        top: `${this.applyMenu.y}px`,
        width: `${this.applyMenu.width || 168}px`,
      }
    },
    /** Req. CO strip: disabled when a future day is selected */
    isReqCoStripDisabled() {
      const dateStr = this.applyMenu.visible
        ? this.applyMenu.date
        : this.selectedApplyDate
      if (!dateStr) return false
      return moment(dateStr).startOf('day').isAfter(moment().startOf('day'), 'day')
    },
  },
  beforeDestroy() {
    this.unbindOutsideClose()
  },
  methods: {
    dayDate(day) {
      return day.date || day.dateStr || null
    },
    isCurrentMonthWorkingDay(day) {
      if (!day || !day.isCurrentMonth) return false
      if (day.isHoliday || day.isWeekOff || day.isCompanyWfh) return false
      return !!this.dayDate(day)
    },
    /** Leave / WFH / Apply Comp Off: today and future */
    isFutureApplyDay(day) {
      if (!this.isCurrentMonthWorkingDay(day)) return false
      const dayMoment = moment(this.dayDate(day)).startOf('day')
      return dayMoment.isSameOrAfter(moment().startOf('day'), 'day')
    },
    /** Request Comp Off: today and past only — never future */
    canRequestCompOffForDay(day) {
      if (!this.isCurrentMonthWorkingDay(day)) return false
      const dayMoment = moment(this.dayDate(day)).startOf('day')
      return dayMoment.isSameOrBefore(moment().startOf('day'), 'day')
    },
    isApplyableDay(day) {
      return this.isFutureApplyDay(day) || this.canRequestCompOffForDay(day)
    },
    isRequestCompOffDateAllowed(dateStr) {
      if (!dateStr) return true
      return moment(dateStr).startOf('day').isSameOrBefore(moment().startOf('day'), 'day')
    },
    onDayClick(event, day) {
      this.$emit('date-click', day)

      if (!this.compact) return

      if (!this.isApplyableDay(day)) {
        this.closeApplyMenu()
        this.selectedApplyDate = null
        return
      }

      const container = this.$refs.calendarContainer
      if (!container) return

      const showFutureApplies = this.isFutureApplyDay(day)
      const showRequestCompOff = this.canRequestCompOffForDay(day)
      const actionCount = (showFutureApplies ? 3 : 0) + (showRequestCompOff ? 1 : 0)
      const isNarrow = container.clientWidth < 420
      const menuWidth = Math.min(isNarrow ? 188 : 168, Math.max(140, container.clientWidth - 8))
      const itemH = isNarrow ? 40 : 34
      const menuHeight = 36 + actionCount * itemH

      const containerRect = container.getBoundingClientRect()
      const cellRect = event.currentTarget.getBoundingClientRect()

      let x = cellRect.left - containerRect.left + cellRect.width / 2 - menuWidth / 2
      let y = cellRect.bottom - containerRect.top + 4

      x = Math.max(4, Math.min(x, containerRect.width - menuWidth - 4))
      if (y + menuHeight > containerRect.height) {
        y = cellRect.top - containerRect.top - menuHeight - 4
      }
      y = Math.max(4, y)

      const dateStr = this.dayDate(day)
      this.selectedApplyDate = dateStr
      this.applyMenu = {
        visible: true,
        date: dateStr,
        label: moment(dateStr).format('DD MMM'),
        x,
        y,
        width: menuWidth,
        showFutureApplies,
        showRequestCompOff,
      }
      this.bindOutsideClose()
    },
    onContainerClick() {
      // clicks on empty calendar chrome close the menu
      this.closeApplyMenu()
    },
    chooseApply(type) {
      const date = this.applyMenu.date
      this.closeApplyMenu()
      this.emitApply(type, date)
    },
    emitApply(type, date = null) {
      if (type === 'request-comp-off') {
        const targetDate = date || this.selectedApplyDate
        if (!this.isRequestCompOffDateAllowed(targetDate)) return
        this.$emit('request-comp-off', targetDate)
        return
      }
      if (type === 'leave') this.$emit('apply-leave', date)
      else if (type === 'wfh') this.$emit('apply-wfh', date)
      else if (type === 'comp-off') this.$emit('apply-comp-off', date)
    },
    closeApplyMenu() {
      if (!this.applyMenu.visible) return
      this.applyMenu.visible = false
      this.applyMenu.date = null
      this.applyMenu.showFutureApplies = false
      this.applyMenu.showRequestCompOff = false
      this.unbindOutsideClose()
    },
    bindOutsideClose() {
      this.unbindOutsideClose()
      this._outsideCloseHandler = (e) => {
        const container = this.$refs.calendarContainer
        if (container && !container.contains(e.target)) {
          this.closeApplyMenu()
        }
      }
      // touchstart + mousedown so popover closes on touch devices (not hover-only)
      document.addEventListener('mousedown', this._outsideCloseHandler)
      document.addEventListener('touchstart', this._outsideCloseHandler, { passive: true })
    },
    unbindOutsideClose() {
      if (this._outsideCloseHandler) {
        document.removeEventListener('mousedown', this._outsideCloseHandler)
        document.removeEventListener('touchstart', this._outsideCloseHandler)
        this._outsideCloseHandler = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.my-view-calendar-card {
  background: #ffffff;
  border: 1px solid #e8ecf0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: auto;
  max-height: none;
}

.my-view-calendar-card.calendar-full-width {
  min-height: 0;
}

/*
 * Compact (Employee View): content-sized card.
 * Do NOT equal-height-stretch the day grid — that crushed weeks via
 * flex:1 / min-height:0 / overflow:hidden. Card may stretch its background
 * (min-height:100%) but the body stays natural height.
 */
.my-view-calendar-card.calendar-compact {
  height: auto;
  min-height: 100%;
  flex: 1 1 auto;
  align-self: stretch;
}

.my-view-calendar-card :deep(.card-body),
.my-view-calendar-card .card-body {
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  height: auto;
  min-height: 0;
  overflow: visible;
  padding: 0.75rem 0.85rem;
}

/* toolbar → weekday+grid → legend → apply strip (all content-sized) */
.calendar-compact :deep(.card-body),
.calendar-compact .card-body {
  display: flex !important;
  flex-direction: column !important;
  flex: 0 0 auto !important;
  height: auto !important;
  min-height: 0;
  overflow: visible !important;
  padding: 0.65rem 0.75rem;
  justify-content: flex-start;
  align-content: flex-start;
}

.calendar-section-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  line-height: 1.2;
}

.calendar-toolbar {
  gap: 0.4rem;
  flex-shrink: 0;
  min-height: 1.75rem;
  margin-bottom: 0.5rem !important;
}

.calendar-compact .calendar-toolbar {
  margin-bottom: 0.4rem !important;
}

.calendar-month-select {
  min-width: 110px;
}

.calendar-year-select {
  min-width: 80px;
}

.calendar-compact .calendar-month-select {
  min-width: 88px;
  font-size: 0.75rem;
}

.calendar-compact .calendar-year-select {
  min-width: 68px;
  font-size: 0.75rem;
}

.calendar-container {
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  overflow: hidden;
  background: #ffffff;
}

.my-view-calendar-container {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: visible;
  position: relative;
}

.calendar-compact .my-view-calendar-container {
  flex: 0 0 auto;
  min-height: auto;
  height: auto;
  max-height: none;
  overflow: visible;
  align-self: stretch;
}

.my-view-calendar-container .calendar-body {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  min-height: auto;
}

.calendar-compact .my-view-calendar-container .calendar-body {
  flex: 0 0 auto;
  min-height: auto;
  overflow: visible;
  height: auto;
  max-height: none;
}

.calendar-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: #f8fafb;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.calendar-day-header {
  padding: 0.4rem 0.2rem;
  text-align: center;
  font-weight: 600;
  font-size: 0.72rem;
  color: #6b7280;
  text-transform: uppercase;
}

.calendar-compact .calendar-day-header {
  padding: 0.3rem 0.05rem;
  font-size: 0.62rem;
}

.calendar-body {
  display: flex;
  flex-direction: column;
}

.calendar-week {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border-bottom: 1px solid #f3f4f6;
  flex: 0 0 auto;
  min-height: 36px;
}

/* Solid week row height — never flex-shrink/collapse */
.calendar-compact .calendar-week {
  flex: 0 0 auto;
  min-height: 36px;
}

.calendar-week:last-child {
  border-bottom: none;
}

.calendar-day {
  min-height: 50px;
  padding: 0.5rem 0.25rem;
  border-right: 1px solid #f3f4f6;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.my-view-calendar-container .calendar-day {
  min-height: 36px;
  height: auto;
  padding: 0.3rem 0.15rem;
}

.calendar-compact .my-view-calendar-container .calendar-day {
  min-height: 36px;
  height: auto;
  padding: 0.2rem 0.05rem;
}

.calendar-day:last-child {
  border-right: none;
}

.calendar-day:hover {
  background: #f9fafb;
}

.calendar-day.day-applyable {
  -webkit-tap-highlight-color: rgba(0, 153, 172, 0.15);
  touch-action: manipulation;
}

.calendar-day.day-applyable:hover,
.calendar-day.day-applyable:active {
  background: #eef7f8;
  box-shadow: inset 0 0 0 1px rgba(0, 153, 172, 0.35);
}

.calendar-day.day-menu-open {
  box-shadow: inset 0 0 0 2px #0099AC;
}

.calendar-day.other-month {
  background: #fafafa;
  color: #d1d5db;
}

.calendar-day.today {
  background: #eff6ff;
  font-weight: 600;
  border: 2px solid #0099AC;
}

.calendar-day.holiday {
  background: #f3e8ff;
}

.calendar-day.company-wfh {
  background: #e0f2fe;
}

/* Absent / Present / Reg styles below take precedence when punch status exists */

.calendar-day.present {
  background: #dcfce7;
}

.calendar-day.leave {
  background: #fef3c7;
}

.calendar-day.maternity-paternity-leave {
  background: #fce7f3;
}

.calendar-day.wfh {
  background: #fef9c3;
}

.calendar-day.regularization {
  background: #fef3c7;
}

.calendar-day.absent {
  background: #fee2e2;
}

.calendar-day.other-leave {
  background: #f3e8ff;
}

.calendar-day.has-biometric {
  position: relative;
}

.calendar-day.has-biometric::after {
  content: '';
  position: absolute;
  bottom: 3px;
  right: 3px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #22c55e;
}

.calendar-compact .calendar-day.has-biometric::after {
  width: 4px;
  height: 4px;
  bottom: 2px;
  right: 2px;
}

.day-number {
  font-weight: 500;
  font-size: 0.78rem;
  color: #374151;
  margin: 0;
  line-height: 1;
}

.calendar-compact .day-number {
  font-size: 0.72rem;
}

.calendar-day.other-month .day-number {
  color: #d1d5db;
  font-weight: 400;
}

.calendar-day.today .day-number {
  color: #007a8a;
}

.calendar-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.65rem;
  font-size: 0.68rem;
  color: #6b7280;
  padding-top: 0.4rem;
  margin-top: 0.5rem !important;
  border-top: 1px solid #eef1f4;
  flex-shrink: 0;
  align-content: flex-start;
  position: relative;
  z-index: 2;
  background: #ffffff;
  min-height: max-content;
}

.calendar-legend-compact {
  gap: 0.25rem 0.45rem;
  font-size: 0.6rem;
  margin-top: 0.35rem !important;
  padding-top: 0.3rem;
  line-height: 1.25;
  flex: 0 0 auto;
  min-height: max-content;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.22rem;
  white-space: nowrap;
}

.legend-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

.calendar-legend-compact .legend-dot {
  width: 6px;
  height: 6px;
}

.present-dot { background: #22c55e; }
.leave-dot { background: #fbbf24; }
.wfh-dot { background: #fef08a; border: 1px solid #eab308; }
.regularization-dot { background: #fbbf24; }
.holiday-dot { background: #a855f7; }
.company-wfh-dot { background: #0ea5e9; }
.absent-dot { background: #ef4444; }
.weekoff-dot { background: #9ca3af; }
.maternity-paternity-dot { background: #f472b6; }

/* Apply strip — content-sized footer under legend */
.calendar-apply-strip {
  flex: 0 0 auto;
  margin-top: 0.4rem;
  padding-top: 0.35rem;
  border-top: 1px solid #eef1f4;
  position: relative;
  z-index: 2;
  background: #ffffff;
  min-height: max-content;
}

.calendar-apply-btn:disabled,
.calendar-apply-btn[disabled] {
  opacity: 0.45;
  cursor: not-allowed;
  border: none;
  color: #9ca3af;
  background: #f3f5f7;
}

.calendar-apply-btn:disabled:hover,
.calendar-apply-btn[disabled]:hover {
  border: none;
  color: #9ca3af;
  background: #f3f5f7;
}

.calendar-apply-strip__hint {
  font-size: 0.6rem;
  color: #9ca3af;
  margin-bottom: 0.3rem;
  line-height: 1.2;
}

.calendar-apply-strip__actions {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.3rem;
}

.calendar-apply-btn {
  border: none;
  border-radius: 6px;
  font-size: 0.62rem;
  font-weight: 600;
  line-height: 1.2;
  padding: 0.28rem 0.2rem;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.calendar-apply-btn--leave {
  background: #f0fafb;
  color: #007a8a;
}

.calendar-apply-btn--leave:hover {
  background: rgba(0, 153, 172, 0.12);
  color: #007a8a;
}

.calendar-apply-btn--wfh {
  background: #fff8f0;
  color: #b36b00;
}

.calendar-apply-btn--wfh:hover {
  background: rgba(255, 159, 67, 0.12);
  color: #b36b00;
}

.calendar-apply-btn--comp-off {
  background: #f0faf4;
  color: #1e7e34;
}

.calendar-apply-btn--comp-off:hover {
  background: rgba(40, 167, 69, 0.1);
  color: #1e7e34;
}

.calendar-apply-btn--req-co {
  background: #f0f5ff;
  color: #0056b3;
  font-weight: 500;
}

.calendar-apply-btn--req-co:hover {
  background: rgba(0, 123, 255, 0.1);
  color: #0056b3;
}

/* Day click popover */
.calendar-day-apply-menu {
  position: absolute;
  z-index: 20;
  width: 168px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  padding: 0.35rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.calendar-day-apply-menu__title {
  font-size: 0.62rem;
  font-weight: 600;
  color: #6b7280;
  padding: 0.2rem 0.4rem 0.3rem;
  border-bottom: 1px solid #f3f4f6;
  margin-bottom: 0.1rem;
}

.calendar-day-apply-menu__item {
  display: flex;
  align-items: center;
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  font-size: 0.72rem;
  font-weight: 500;
  color: #374151;
  border-radius: 5px;
  padding: 0.35rem 0.45rem;
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.calendar-day-apply-menu__item:hover {
  background: #f0fafb;
  color: #007a8a;
}

.calendar-day-apply-menu__item--muted {
  color: #6b7280;
  font-weight: 400;
}

@media (max-width: 991px) {
  /* Mobile: QuickActionsCard is primary — hide duplicate calendar action grid */
  .calendar-apply-strip {
    display: none !important;
  }

  .my-view-calendar-card {
    height: auto;
    max-height: none;
    min-height: 0;
  }

  .my-view-calendar-card.calendar-compact {
    min-height: 0;
  }

  .calendar-compact :deep(.card-body),
  .calendar-compact .card-body {
    display: flex !important;
    flex-direction: column !important;
    height: auto !important;
    overflow: visible !important;
    padding: 0.55rem 0.6rem;
  }

  .calendar-toolbar {
    gap: 0.35rem;
  }

  .calendar-compact .calendar-month-select {
    min-width: 96px;
  }

  .calendar-compact .calendar-year-select {
    min-width: 72px;
  }

  .my-view-calendar-container .calendar-day {
    min-height: 38px;
    padding: 0.25rem 0.15rem;
  }

  .calendar-compact .my-view-calendar-container .calendar-day {
    min-height: 38px;
  }

  .calendar-compact .calendar-week {
    min-height: 38px;
    flex: 0 0 auto;
  }

  .day-number {
    font-size: 0.78rem;
  }

  .calendar-day-header {
    padding: 0.3rem 0.1rem;
    font-size: 0.68rem;
  }

  .calendar-legend,
  .calendar-legend-compact {
    gap: 0.35rem 0.55rem;
    font-size: 0.68rem;
    line-height: 1.3;
  }

  .calendar-day-apply-menu {
    width: min(188px, calc(100% - 8px));
    padding: 0.4rem;
    z-index: 30;
  }

  .calendar-day-apply-menu__title {
    font-size: 0.68rem;
    padding: 0.25rem 0.45rem 0.35rem;
  }

  .calendar-day-apply-menu__item {
    min-height: 40px;
    font-size: 0.8rem;
    padding: 0.45rem 0.55rem;
  }
}

@media (max-width: 575px) {
  .calendar-compact :deep(.card-body),
  .calendar-compact .card-body {
    padding: 0.5rem 0.5rem;
  }

  .calendar-compact .my-view-calendar-container .calendar-day {
    min-height: 36px;
  }

  .calendar-compact .calendar-week {
    min-height: 36px;
  }
}
</style>
